<template>
  <div class="group-products-page">
    <div class="page-head">
      <el-button size="small" icon="el-icon-back" @click="$emit('back')">返回</el-button>
      <div class="page-head-text">
        <div class="page-title">交易品种配置</div>
        <div class="page-subtitle">为该交易组别配置关联的交易品种</div>
      </div>
    </div>

    <div class="group-summary-card">
      <div class="summary-item">
        <span class="summary-label">交易组别</span>
        <span class="summary-value summary-path">{{ group.mt5GroupPath || '-' }}</span>
      </div>
      <div class="summary-divider"></div>
      <div class="summary-item">
        <span class="summary-label">组别名称</span>
        <span class="summary-value">{{ group.groupName || '-' }}</span>
      </div>
      <div class="summary-divider"></div>
      <div class="summary-item">
        <span class="summary-label">默认币种</span>
        <span class="summary-value">{{ group.currency || '-' }}</span>
      </div>
    </div>

    <div class="filter-card">
      <div class="filter-grid">
        <div class="filter-item">
          <div class="filter-label">产品分类</div>
          <el-select v-model="searchForm.bigCategoryName" clearable size="small" placeholder="全部" @change="handleSearch">
            <el-option label="全部" :value="null" />
            <el-option
              v-for="item in categoryOptions"
              :key="item.name"
              :label="item.name"
              :value="item.name"
            />
          </el-select>
        </div>
        <div class="filter-item">
          <div class="filter-label">产品CODE</div>
          <el-input v-model.trim="searchForm.productCode" size="small" placeholder="请输入产品CODE" @keyup.enter.native="handleSearch" />
        </div>
        <div class="filter-item">
          <div class="filter-label">产品名称</div>
          <el-input v-model.trim="searchForm.productName" size="small" placeholder="请输入产品名称" @keyup.enter.native="handleSearch" />
        </div>
        <div class="filter-item">
          <div class="filter-label">是否启用</div>
          <el-select v-model="searchForm.isEnabled" clearable size="small" placeholder="全部" @change="handleSearch">
            <el-option label="全部" :value="null" />
            <el-option label="是" :value="1" />
            <el-option label="否" :value="2" />
          </el-select>
        </div>
        <div class="filter-actions">
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">搜索</el-button>
          <el-button size="small" @click="resetSearch">重置</el-button>
        </div>
      </div>
    </div>

    <div class="category-card">
      <div
        class="category-chip"
        :class="{ active: searchForm.bigCategoryName == null }"
        @click="selectCategory(null)"
      >
        <span class="category-dot category-dot-all"></span>
        全部分类
      </div>
      <div
        v-for="item in categoryOptions"
        :key="item.name"
        class="category-chip"
        :class="{ active: searchForm.bigCategoryName === item.name }"
        @click="selectCategory(item.name)"
      >
        <span class="category-dot" :style="{ background: categoryColor(item.name) }"></span>
        {{ item.name }}
        <span class="chip-count">{{ item.count }}</span>
      </div>
    </div>

    <div class="table-card" v-loading="loading">
      <div class="table-card-head">
        <div class="table-card-title">关联产品列表</div>
        <div class="table-card-actions">
          <el-button v-auth="'business:tradeGroup:product'" type="primary" size="mini" icon="el-icon-plus" @click="openPicker">新增产品</el-button>
          <el-button v-auth="'business:tradeGroup:product'" size="mini" icon="el-icon-delete" class="danger-btn" @click="handleBatchDelete">批量删除</el-button>
        </div>
      </div>
      <div class="table-scroll">
        <el-table
          ref="productTable"
          :data="pagedProducts"
          border
          size="small"
          row-key="id"
          class="product-table"
          @selection-change="handleSelectionChange"
        >
          <el-table-column type="selection" width="40" align="center" />
          <el-table-column label="排序" min-width="86" align="center">
            <template slot-scope="scope">
              <el-input-number
                v-auth="'business:tradeGroup:product'"
                v-model="scope.row.sort"
                size="mini"
                :min="0"
                :step="1"
                :precision="0"
                :controls="false"
                class="inline-number"
                @change="handleSortChange(scope.row)"
              />
            </template>
          </el-table-column>
          <el-table-column prop="bigCategoryName" label="产品分类" min-width="84" align="center">
            <template slot-scope="scope">
              <span class="category-text">{{ scope.row.bigCategoryName || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="productCode" label="产品CODE" min-width="100" show-overflow-tooltip>
            <template slot-scope="scope">
              <span class="code-text">{{ scope.row.productCode }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="productName" label="产品名称" min-width="106" show-overflow-tooltip />
          <el-table-column prop="currencyBase" label="基础货币" min-width="76" align="center" />
          <el-table-column prop="currencyProfit" label="盈亏货币" min-width="76" align="center" />
          <el-table-column prop="digits" label="小数位" min-width="62" align="center" />
          <el-table-column label="PIP" min-width="64" align="center">
            <template slot-scope="scope">
              {{ formatPointValue(scope.row.pointValue) }}
            </template>
          </el-table-column>
          <el-table-column label="默认杠杆" min-width="104" align="center">
            <template slot-scope="scope">
              <el-input
                v-auth="'business:tradeGroup:product'"
                v-model="scope.row.leverageNumber"
                size="mini"
                class="inline-leverage"
                placeholder="100"
                @change="handleLeverageChange(scope.row)"
              >
                <template slot="prepend">1:</template>
              </el-input>
            </template>
          </el-table-column>
          <el-table-column label="启用" min-width="64" align="center">
            <template slot-scope="scope">
              <el-switch
                v-auth="'business:tradeGroup:product'"
                v-model="scope.row.isEnabled"
                :active-value="1"
                :inactive-value="2"
                @change="handleEnabledChange(scope.row)"
              />
            </template>
          </el-table-column>
          <el-table-column
            label="操作"
            min-width="96"
            align="center"
            class-name="col-op"
            label-class-name="col-op"
          >
            <template slot-scope="scope">
              <el-button v-auth="'business:tradeGroup:product'" type="text" size="mini" @click="openDetail(scope.row)">查看</el-button>
              <el-button v-auth="'business:tradeGroup:product'" type="text" size="mini" class="danger-link" @click="handleDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="table-footer">
        <div class="table-total">共 {{ filteredProducts.length }} 条</div>
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pageNum"
          :page-sizes="[10, 15, 20, 50]"
          :page-size="pageSize"
          layout="sizes, prev, pager, next"
          :total="filteredProducts.length"
        />
      </div>
    </div>

    <el-dialog :title="detailTitle" :visible.sync="detailVisible" width="700px" class="product-detail-dialog">
      <div v-loading="detailLoading" class="detail-body">
        <template v-if="detail">
          <div class="group-config-strip">
            <div class="group-config-item">
              <span class="group-config-label">产品分类</span>
              <span class="group-config-value">{{ detail.bigCategoryName || '-' }}</span>
            </div>
            <div class="group-config-item">
              <span class="group-config-label">排序</span>
              <span class="group-config-value">{{ detail.sort == null ? '-' : detail.sort }}</span>
            </div>
            <div class="group-config-item">
              <span class="group-config-label">默认杠杆</span>
              <span class="group-config-value">{{ detail.defaultLeverage || '-' }}</span>
            </div>
            <div class="group-config-item">
              <span class="group-config-label">启用</span>
              <el-tag size="mini" :type="detail.isEnabled === 1 ? 'success' : 'info'">
                {{ detail.isEnabled === 1 ? '启用' : (detail.isEnabled === 2 ? '禁用' : '-') }}
              </el-tag>
            </div>
          </div>

          <el-tabs v-model="detailTab" class="detail-tabs">
            <el-tab-pane label="常规" name="general">
              <div class="pc-form">
                <div class="pc-form-item">
                  <label>交易品种</label>
                  <el-input :value="detail.productCode" size="small" disabled />
                </div>
                <div class="pc-form-row">
                  <div class="pc-form-item pc-item-narrow">
                    <label>点差</label>
                    <el-input :value="displayValue(detail.spreadDiff)" size="small" disabled />
                  </div>
                  <div class="pc-form-item">
                    <label>点差平衡 <span class="pc-inline-hint">{{ spreadBalanceText }}</span></label>
                    <el-slider
                      v-model="spreadBalanceValue"
                      :min="-spreadBalanceSpan"
                      :max="spreadBalanceSpan"
                      disabled
                    />
                  </div>
                </div>

                <div class="pc-section-title">单量设置</div>
                <div class="pc-form-row">
                  <div class="pc-form-item"><label>最小单量(手)</label><el-input :value="displayValue(detail.volumeMin)" size="small" disabled /></div>
                  <div class="pc-form-item"><label>最大单量(手)</label><el-input :value="displayValue(detail.volumeMax)" size="small" disabled /></div>
                  <div class="pc-form-item"><label>步长(手)</label><el-input :value="displayValue(detail.volumeStep)" size="small" disabled /></div>
                </div>
                <div class="pc-form-item">
                  <label>限制交易量(手)</label>
                  <el-input :value="displayValue(detail.volumeLimit)" size="small" disabled />
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane label="交易" name="trade">
              <div class="pc-form">
                <div class="pc-form-row">
                  <div class="pc-form-item"><label>交易模式</label><el-input :value="detail.tradeModeName" size="small" disabled /></div>
                  <div class="pc-form-item"><label>成交模式</label><el-input :value="detail.fillModeName" size="small" disabled /></div>
                </div>
                <div class="pc-form-row">
                  <div class="pc-form-item"><label>最小止损(点)</label><el-input :value="displayValue(detail.stopsLevel)" size="small" disabled /></div>
                  <div class="pc-form-item"><label>最大止损(点)</label><el-input :value="displayValue(detail.freezeLevel)" size="small" disabled /></div>
                </div>
                <div class="pc-form-item">
                  <label>合约大小</label>
                  <el-input :value="displayValue(detail.contractSize)" size="small" disabled />
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane label="执行" name="execution">
              <div class="pc-form">
                <div class="pc-form-item">
                  <label>执行模式</label>
                  <el-input :value="detail.execModeName" size="small" disabled />
                </div>
                <div class="pc-form-row">
                  <div class="pc-form-item"><label>最大滑点(点)</label><el-input :value="displayValue(detail.ieSlipLosing)" size="small" disabled /></div>
                  <div class="pc-form-item"><label>报价超时(秒)</label><el-input :value="displayValue(detail.quoteTimeoutSec)" size="small" disabled /></div>
                </div>
                <div class="pc-form-item pc-form-check">
                  <el-checkbox :value="detail.allowRequote === 1" disabled>允许重新报价</el-checkbox>
                </div>
                <div class="pc-form-row">
                  <div class="pc-form-item"><label>盈利滑点</label><el-input :value="displayValue(detail.ieSlipProfit)" size="small" disabled /></div>
                  <div class="pc-form-item"><label>即时执行超时(秒)</label><el-input :value="displayValue(detail.ieTimeout)" size="small" disabled /></div>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane label="保证金" name="margin">
              <div class="pc-form">
                <div class="pc-form-item">
                  <label>保证金货币</label>
                  <el-input :value="detail.currencyMargin || '-'" size="small" disabled />
                </div>
                <div class="pc-form-item">
                  <label>保证金标识</label>
                  <el-input :value="displayValue(detail.marginFlags)" size="small" disabled />
                </div>
                <div class="pc-form-row">
                  <div class="pc-form-item"><label>对冲保证金(%)</label><el-input :value="displayValue(detail.marginHedged)" size="small" disabled /></div>
                  <div class="pc-form-item"><label>保证金维持比例(%)</label><el-input :value="displayValue(detail.marginMaintenance)" size="small" disabled /></div>
                </div>
                <div class="pc-form-row">
                  <div class="pc-form-item"><label>初始保证金</label><el-input :value="displayValue(detail.marginInitial)" size="small" disabled /></div>
                  <div class="pc-form-item"><label>货币保证金系数</label><el-input :value="displayValue(detail.marginCurrency)" size="small" disabled /></div>
                </div>
              </div>
            </el-tab-pane>

            <el-tab-pane label="过夜利息" name="swap">
              <div class="pc-form">
                <div class="pc-form-item">
                  <label>Swap类型</label>
                  <el-input :value="detail.swapModeName" size="small" disabled />
                </div>
                <div class="pc-form-row">
                  <div class="pc-form-item"><label>Swap多头</label><el-input :value="displayValue(detail.swapLong)" size="small" disabled /></div>
                  <div class="pc-form-item"><label>Swap空头</label><el-input :value="displayValue(detail.swapShort)" size="small" disabled /></div>
                </div>
                <div class="pc-form-row">
                  <div class="pc-form-item"><label>周三Swap倍数</label><el-input :value="displayValue(detail.swapRateWednesday)" size="small" disabled /></div>
                  <div class="pc-form-item"><label>周末Swap倍数</label><el-input :value="displayValue(detail.swapRateSunday)" size="small" disabled /></div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>

          <div class="detail-path-row">
            <span class="pc-path-label">MT5配置路径</span>
            <span class="code-text">{{ detail.mt5ConfigPath || '-' }}</span>
          </div>
        </template>
      </div>
      <div slot="footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog title="新增产品" :visible.sync="pickerVisible" width="760px" class="product-picker-dialog" @opened="handlePickerOpened">
      <div class="picker-filter">
        <el-select v-model="pickerSearch.bigCategoryName" clearable size="small" placeholder="全部分类" class="picker-category">
          <el-option label="全部分类" :value="null" />
          <el-option v-for="name in pickerCategoryOptions" :key="name" :label="name" :value="name" />
        </el-select>
        <el-input
          v-model.trim="pickerSearch.keyword"
          size="small"
          class="picker-keyword"
          placeholder="请输入产品CODE或名称"
          @keyup.enter.native="handlePickerSearch"
        />
        <el-button type="primary" size="small" icon="el-icon-search" @click="handlePickerSearch">搜索</el-button>
      </div>
      <el-table
        ref="pickerTable"
        v-loading="pickerLoading"
        :data="pickerList"
        border
        size="small"
        row-key="id"
        height="360"
        style="width: 100%;"
        @selection-change="handlePickerSelectionChange"
      >
        <el-table-column type="selection" width="42" align="center" :reserve-selection="true" />
        <el-table-column prop="bigCategoryName" label="产品分类" min-width="100" align="center">
          <template slot-scope="scope">
            <span class="category-text">{{ scope.row.bigCategoryName || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="productCode" label="产品CODE" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="code-text">{{ scope.row.productCode }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="productName" label="产品名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="currencyBase" label="基础货币" width="90" align="center" />
        <el-table-column prop="currencyProfit" label="盈亏货币" width="90" align="center" />
        <el-table-column prop="digits" label="小数位" width="76" align="center" />
      </el-table>
      <div class="table-footer">
        <div class="table-total">共 {{ pickerTotal }} 条，已选 {{ pickerSelection.length }} 个</div>
        <el-pagination
          @size-change="handlePickerSizeChange"
          @current-change="handlePickerCurrentChange"
          :current-page="pickerPageNum"
          :page-sizes="[10, 20, 50]"
          :page-size="pickerPageSize"
          layout="sizes, prev, pager, next"
          :total="pickerTotal"
        />
      </div>
      <div slot="footer">
        <el-button size="small" @click="pickerVisible = false">取消</el-button>
        <el-button v-auth="'business:tradeGroup:product'" type="primary" size="small" :loading="pickerSaving" @click="handlePickerConfirm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  tradeGroupProductList,
  tradeGroupProductCandidateList,
  tradeGroupProductDetail,
  tradeGroupProductAdd,
  tradeGroupProductRemove,
  tradeGroupProductUpdate
} from '@/api/tradeGroup'

function parseLeverageNumber(text) {
  if (text == null || text === '' || text === '-') {
    return undefined
  }
  const parts = String(text).split(':')
  const value = parseInt(parts[parts.length - 1], 10)
  return isNaN(value) ? undefined : value
}

export default {
  name: 'TradeGroupProducts',
  props: {
    group: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      loading: false,
      sourceList: [],
      selectedRows: [],
      pageNum: 1,
      pageSize: 15,
      searchForm: {
        bigCategoryName: null,
        productCode: '',
        productName: '',
        isEnabled: null
      },
      detailVisible: false,
      detailLoading: false,
      detail: null,
      detailTab: 'general',
      spreadBalanceValue: 0,
      pickerVisible: false,
      pickerLoading: false,
      pickerSaving: false,
      pickerList: [],
      pickerTotal: 0,
      pickerPageNum: 1,
      pickerPageSize: 10,
      pickerSelection: [],
      pickerSearch: {
        bigCategoryName: null,
        keyword: ''
      }
    }
  },
  computed: {
    detailTitle() {
      const code = this.detail && this.detail.productCode
      return code ? `交易品种: ${code}（查看）` : '关联产品详情'
    },
    // 点差平衡滑块边界：|平衡值| 超过 点差/2 时一侧点差会成负数，故以此为滑块两端
    spreadBalanceSpan() {
      const total = Number(this.detail && this.detail.spreadDiff) || 0
      return Math.max(1, Math.round(Math.abs(total) / 2))
    },
    // MT5 口径：基准值 = 点差/2，偏移 = |点差平衡|，
    // 平衡为负时 Bid 侧加宽、Ask 侧收窄，为正时相反；两侧之和恒等于点差。
    // 原型里的「bal 百分比切分」是模拟数据时的假口径，真实 SpreadDiffBalance 是点值偏移
    spreadBalanceText() {
      const total = Number(this.detail && this.detail.spreadDiff) || 0
      const balance = Number(this.spreadBalanceValue) || 0
      const bid = Math.round(total / 2 - balance)
      const ask = total - bid
      return `${bid}bid / ${ask}ask`
    },
    // 分类选项取自该组实际关联品种，避免原型里的写死清单与真实数据脱节
    categoryOptions() {
      const counter = new Map()
      this.sourceList.forEach(item => {
        const name = item.bigCategoryName
        if (!name) {
          return
        }
        counter.set(name, (counter.get(name) || 0) + 1)
      })
      return Array.from(counter, ([name, count]) => ({ name, count }))
    },
    // 选择器可选分类 = 已关联分类 ∪ 候选分类，保证筛选后列表变化时选项不消失
    pickerCategoryOptions() {
      const names = new Set()
      this.sourceList.concat(this.pickerList).forEach(item => {
        if (item && item.bigCategoryName) {
          names.add(item.bigCategoryName)
        }
      })
      return Array.from(names)
    },
    filteredProducts() {
      return this.sourceList.filter(item => {
        if (this.searchForm.bigCategoryName != null && item.bigCategoryName !== this.searchForm.bigCategoryName) {
          return false
        }
        if (this.searchForm.productCode && String(item.productCode || '').toUpperCase().indexOf(this.searchForm.productCode.toUpperCase()) === -1) {
          return false
        }
        if (this.searchForm.productName && String(item.productName || '').indexOf(this.searchForm.productName) === -1) {
          return false
        }
        if (this.searchForm.isEnabled != null && item.isEnabled !== this.searchForm.isEnabled) {
          return false
        }
        return true
      })
    },
    pagedProducts() {
      const start = (this.pageNum - 1) * this.pageSize
      return this.filteredProducts.slice(start, start + this.pageSize)
    }
  },
  watch: {
    'group.id': {
      immediate: true,
      handler() {
        this.resetState()
        this.fetchList()
      }
    }
  },
  methods: {
    resetState() {
      this.pageNum = 1
      this.selectedRows = []
      this.detail = null
      this.detailTab = 'general'
      this.detailVisible = false
      this.pickerVisible = false
      this.resetSearch()
    },
    fetchList() {
      if (!this.group || !this.group.id) {
        this.sourceList = []
        return
      }
      this.loading = true
      tradeGroupProductList(this.group.id).then(res => {
        if (res.code === 0) {
          this.sourceList = (res.result || []).map(item => {
            item.leverageNumber = parseLeverageNumber(item.defaultLeverage)
            return item
          })
          this.selectedRows = []
        }
      }).finally(() => {
        this.loading = false
      })
    },
    handleSearch() {
      this.pageNum = 1
    },
    resetSearch() {
      this.searchForm = {
        bigCategoryName: null,
        productCode: '',
        productName: '',
        isEnabled: null
      }
      this.pageNum = 1
    },
    selectCategory(name) {
      this.searchForm.bigCategoryName = name
      this.pageNum = 1
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.pageNum = 1
    },
    handleCurrentChange(page) {
      this.pageNum = page
    },
    handleSelectionChange(rows) {
      this.selectedRows = rows || []
    },
    // 内联编辑：勾选/输入即持久化，失败时重载还原
    persistUpdate(payload) {
      tradeGroupProductUpdate(payload).then(() => {
        this.$message.success('保存成功')
        this.fetchList()
      }).catch(() => {
        this.fetchList()
      })
    },
    handleSortChange(row) {
      if (row.sort == null || isNaN(Number(row.sort))) {
        return
      }
      this.persistUpdate({ id: row.id, sort: Number(row.sort) })
    },
    handleLeverageChange(row) {
      const leverage = parseInt(row.leverageNumber, 10)
      if (isNaN(leverage) || leverage <= 0) {
        this.$message.warning('默认杠杆须为大于 0 的整数')
        this.fetchList()
        return
      }
      this.persistUpdate({ id: row.id, leverage })
    },
    handleEnabledChange(row) {
      this.persistUpdate({ id: row.id, isEnabled: row.isEnabled })
    },
    handleDelete(row) {
      this.$confirm(`确定移除产品「${row.productCode}」？`, '提示', { type: 'warning' }).then(() => {
        return tradeGroupProductRemove({ groupId: this.group.id, ids: [row.id] })
      }).then(() => {
        this.$message.success('移除成功')
        this.fetchList()
      }).catch(() => {})
    },
    handleBatchDelete() {
      if (!this.selectedRows.length) {
        this.$message.warning('请先勾选要删除的产品')
        return
      }
      const count = this.selectedRows.length
      this.$confirm(`确定删除选中的 ${count} 个产品？`, '提示', { type: 'warning' }).then(() => {
        return tradeGroupProductRemove({ groupId: this.group.id, ids: this.selectedRows.map(item => item.id) })
      }).then(() => {
        this.$message.success('删除成功')
        this.fetchList()
      }).catch(() => {})
    },
    openDetail(row) {
      this.detail = null
      this.detailTab = 'general'
      this.spreadBalanceValue = 0
      this.detailVisible = true
      this.detailLoading = true
      tradeGroupProductDetail(row.id).then(res => {
        if (res.code === 0) {
          this.detail = res.result
          if (this.detail && this.detail.spreadDiffBalance != null) {
            this.spreadBalanceValue = Number(this.detail.spreadDiffBalance)
          }
        }
      }).finally(() => {
        this.detailLoading = false
      })
    },
    openPicker() {
      this.pickerSearch = { bigCategoryName: null, keyword: '' }
      this.pickerPageNum = 1
      this.pickerSelection = []
      this.pickerVisible = true
      this.fetchCandidates()
    },
    handlePickerOpened() {
      if (this.$refs.pickerTable) {
        this.$refs.pickerTable.clearSelection()
      }
    },
    fetchCandidates() {
      if (!this.group || !this.group.id) {
        return
      }
      this.pickerLoading = true
      tradeGroupProductCandidateList({
        groupId: this.group.id,
        bigCategoryName: this.pickerSearch.bigCategoryName,
        keyword: this.pickerSearch.keyword,
        pageNum: this.pickerPageNum,
        pageSize: this.pickerPageSize
      }).then(res => {
        if (res.code === 0) {
          this.pickerList = (res.result && res.result.list) || []
          this.pickerTotal = (res.result && res.result.total) || 0
        }
      }).finally(() => {
        this.pickerLoading = false
      })
    },
    handlePickerSearch() {
      this.pickerPageNum = 1
      this.fetchCandidates()
    },
    handlePickerSizeChange(size) {
      this.pickerPageSize = size
      this.pickerPageNum = 1
      this.fetchCandidates()
    },
    handlePickerCurrentChange(page) {
      this.pickerPageNum = page
      this.fetchCandidates()
    },
    handlePickerSelectionChange(rows) {
      this.pickerSelection = rows || []
    },
    handlePickerConfirm() {
      if (!this.pickerSelection.length) {
        this.$message.warning('请至少选择一个产品')
        return
      }
      const count = this.pickerSelection.length
      this.pickerSaving = true
      tradeGroupProductAdd({
        groupId: this.group.id,
        productIds: this.pickerSelection.map(item => item.id)
      }).then(() => {
        this.$message.success(`已添加 ${count} 个产品`)
        this.pickerVisible = false
        this.fetchList()
      }).catch(() => {}).finally(() => {
        this.pickerSaving = false
      })
    },
    formatPointValue(value) {
      return value == null ? '-' : value
    },
    displayValue(value) {
      return value == null ? '-' : value
    },
    categoryColor(name) {
      const colorMap = {
        贵金属: '#f59e0b',
        外汇: '#3b82f6',
        指数: '#8b5cf6',
        股指: '#8b5cf6',
        能源: '#10b981',
        加密货币: '#f97316',
        美股: '#06b6d4',
        港股: '#ec4899'
      }
      return colorMap[name] || '#6b7280'
    }
  }
}
</script>

<style scoped>
.group-products-page {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 0 0;
}

.page-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.page-head-text {
  display: flex;
  flex-direction: column;
}

.page-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  line-height: 22px;
}

.page-subtitle {
  margin-top: 4px;
  font-size: 12px;
  color: #8a94a6;
}

.group-summary-card {
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #e8f0fe 0%, #d4e4fd 100%);
  border: 1px solid #dde8ff;
  border-radius: 8px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 22px;
}

.summary-divider {
  width: 1px;
  height: 20px;
  background: #dde8ff;
}

.summary-label {
  font-size: 12px;
  color: #97a3b6;
}

.summary-value {
  font-size: 14px;
  font-weight: 500;
  color: #344054;
}

.summary-path {
  font-family: Menlo, Consolas, monospace;
  font-weight: 600;
  color: #2f80ed;
}

.filter-card,
.category-card,
.table-card {
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 10px;
}

.filter-card {
  padding: 16px 18px 8px;
}

.filter-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 16px;
  align-items: flex-end;
}

.filter-item {
  width: 180px;
}

.filter-label {
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #606d80;
}

.filter-actions {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding-bottom: 1px;
}

.category-card {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-top: 12px;
  margin-bottom: 12px;
  padding: 12px 16px;
}

.category-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 13px;
  color: #667085;
  background: #fff;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-chip.active {
  color: #344054;
  background: #f8fafc;
  border-color: #d8e1ef;
  box-shadow: inset 0 0 0 1px rgba(216, 225, 239, 0.45);
}

.category-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.category-dot-all {
  background: #6b7280;
}

.chip-count {
  font-size: 12px;
  color: #98a2b3;
}

.table-card {
  padding: 14px 16px 12px;
}

/* 列宽合计超出容器时由本层横向滚动：外层 .app-main 是 overflow:hidden，
   若让 el-table 自身撑出视口，滚动条会落在屏幕外导致右侧列看不到 */
.table-scroll {
  width: 100%;
  overflow-x: auto;
}

.table-card-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.table-card-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.table-card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.danger-btn {
  color: #dc2626;
  border-color: #dc2626;
}

.category-text {
  color: #667085;
}

.code-text {
  font-family: Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 500;
}

.danger-link {
  color: #dc2626;
}

.table-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.table-total {
  font-size: 13px;
  color: #667085;
}

.detail-body {
  min-height: 120px;
  max-height: 460px;
  overflow-y: auto;
}

.group-config-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 24px;
  margin-bottom: 10px;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #ebeef5;
  border-radius: 8px;
}

.group-config-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.group-config-label {
  font-size: 12px;
  color: #97a3b6;
}

.group-config-value {
  font-size: 13px;
  font-weight: 500;
  color: #344054;
}

.pc-form {
  padding: 2px 2px 4px;
}

.pc-form-item {
  margin-bottom: 14px;
}

.pc-form-item > label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  color: #606d80;
}

.pc-form-row {
  display: flex;
  gap: 12px;
}

.pc-form-row .pc-form-item {
  flex: 1;
}

.pc-item-narrow {
  flex: 0 0 120px;
}

.pc-section-title {
  margin: 4px 0 14px;
  padding-left: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  border-left: 3px solid #2f80ed;
}

.pc-inline-hint {
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  color: #2f80ed;
}

.pc-form-check {
  display: flex;
  align-items: center;
}

.detail-path-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px dashed #ebeef5;
}

.pc-path-label {
  font-size: 12px;
  color: #97a3b6;
}

::v-deep .detail-tabs .el-tabs__header {
  margin-bottom: 14px;
}

::v-deep .detail-tabs .el-tabs__item {
  font-size: 13px;
}

.picker-filter {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.picker-category {
  width: 160px;
}

.picker-keyword {
  width: 240px;
}

/* 列宽下限：容器更宽时表格铺满，更窄时由 .table-scroll 出横向滚动条。
   el-table 自带 max-width:100%，此处显式放开，避免与 min-width 的优先级纠葛 */
::v-deep .table-scroll .product-table {
  min-width: 962px;
  max-width: none;
}

/* 操作列吸边：12 列在窄窗口下必然横向滚动，操作列需常驻右侧可见。
   不能用 el-table 的 fixed="right"——它只跟随表格自身滚动，
   而此处滚动发生在外层 .table-scroll 上，fixed 层会被留在表格右缘（视口外）。
   改用 position:sticky，并把 el-table / wrapper 的 overflow 放开：
   本表无纵向滚动（未设 max-height），放开不会引入额外滚动条，
   但能让 sticky 的定位参照从 .el-table 提升到真正滚动的 .table-scroll */
::v-deep .table-scroll .product-table,
::v-deep .table-scroll .el-table__header-wrapper,
::v-deep .table-scroll .el-table__body-wrapper {
  overflow: visible;
}

::v-deep .table-scroll .product-table .col-op {
  position: -webkit-sticky;
  position: sticky;
  right: 0;
  z-index: 3;
  background: #fff;
  box-shadow: -6px 0 6px -4px rgba(0, 0, 0, 0.12);
}

::v-deep .table-scroll .product-table .el-table__header-wrapper th.col-op {
  background: #f9fafb;
}

::v-deep .product-table .el-table__header-wrapper th {
  background: #f9fafb;
  color: #909399;
  font-weight: 500;
  height: 40px;
  padding-top: 0;
  padding-bottom: 0;
  font-size: 12px;
}

::v-deep .product-table .el-table__body-wrapper td {
  color: #344054;
  padding-top: 10px;
  padding-bottom: 10px;
  font-size: 13px;
}

::v-deep .filter-item .el-input__inner,
::v-deep .filter-item .el-select .el-input__inner {
  height: 36px;
  line-height: 36px;
}

::v-deep .product-table .inline-number {
  width: 64px;
}

::v-deep .product-table .inline-number .el-input__inner {
  padding-left: 6px;
  padding-right: 6px;
  text-align: center;
}

::v-deep .product-table .inline-leverage .el-input-group__prepend {
  padding: 0 5px;
  font-size: 12px;
}

::v-deep .product-table .inline-leverage .el-input__inner {
  padding-left: 6px;
  padding-right: 6px;
  text-align: center;
  height: 28px;
  line-height: 28px;
}

/* 弹窗不超过视口，窄屏时跟着缩 */
::v-deep .product-detail-dialog .el-dialog,
::v-deep .product-picker-dialog .el-dialog {
  max-width: 92vw;
}

@media screen and (max-width: 1200px) {
  .filter-item {
    width: calc(50% - 8px);
  }

  .filter-actions {
    width: 100%;
    justify-content: flex-end;
  }

  .group-summary-card {
    gap: 10px 14px;
  }

  .summary-divider {
    display: none;
  }
}

@media screen and (max-width: 768px) {
  .filter-item {
    width: 100%;
  }

  .table-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .pc-form-row {
    flex-wrap: wrap;
    gap: 0;
  }

  .pc-form-row .pc-form-item,
  .pc-item-narrow {
    flex: 0 0 100%;
  }
}
</style>
